import React, { useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { CameraIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import DepartmentSelect from '../common/DepartmentSelect';
import RoleSelect from '../common/RoleSelect';
import LocationSelect from '../common/LocationSelect';
import MarketingConsentSwitch from './MarketingConsentSwitch';
import { authService } from '../../services/authService';
import { uploadFile, useImageUrl } from '../../services/uploads';
import type { AuthUser } from '../../types/identity';

const MAX_AVATAR_SIZE = 3.1 * 1024 * 1024;
const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

interface ProfileFormData {
  full_name: string;
  email: string;
  department_id: string;
  role_id: string;
  location_id: string;
}

const FloatingField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  readOnly?: boolean;
  /** Shown under the label on read-only fields so they explain themselves. */
  readOnlyHint?: string;
  className?: string;
}> = ({ label, value, onChange, type = 'text', readOnly = false, readOnlyHint, className = '' }) => (
  <div className={`relative ${className}`}>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      readOnly={readOnly}
      placeholder=" "
      title={readOnly ? readOnlyHint : undefined}
      className={`peer w-full h-14 px-3.5 pt-5 pb-2 text-sm border rounded-xl outline-none transition-colors ${
        readOnly
          ? 'bg-background-secondary border-border-primary/10 cursor-not-allowed text-text-secondary'
          : 'bg-surface-primary dark:bg-surface-elevated border-border-primary/20 focus:border-primary-500 text-text-primary'
      }`}
    />
    <label className="absolute left-3.5 top-1.5 flex items-center gap-1 text-xs font-medium text-text-secondary pointer-events-none">
      {label}
      {readOnly && <LockClosedIcon className="h-3 w-3" aria-hidden="true" />}
    </label>
    {readOnly && readOnlyHint && (
      <p className="mt-1 text-[11px] text-text-tertiary">{readOnlyHint}</p>
    )}
  </div>
);

const GeneralSettings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarKey, setAvatarKey] = useState<string | null>(user?.avatar_url ?? null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  // Stored avatar_url is an S3 key — resolve it to a short-lived presigned URL.
  const [signedAvatarUrl] = useImageUrl(avatarKey);
  const avatarSrc = localPreview ?? signedAvatarUrl;

  const [publicProfile, setPublicProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<ProfileFormData>({
    full_name: `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim(),
    email: user?.email ?? '',
    department_id: '',
    role_id: '',
    location_id: '',
  });

  // Fetch fresh profile on mount — the cached AuthContext user doesn't carry
  // membership attribute IDs (role/department/location).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authService.me();
        if (cancelled) return;
        const fetched = res?.data?.user;
        if (!fetched) return;
        setForm({
          full_name: `${fetched.first_name ?? ''} ${fetched.last_name ?? ''}`.trim(),
          email: fetched.email ?? '',
          department_id: fetched.department_id ?? '',
          role_id: fetched.role_id ?? '',
          location_id: fetched.location_id ?? '',
        });
        setAvatarKey(fetched.avatar_url ?? null);
      } catch {
        // Non-fatal — form falls back to the cached AuthContext user.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      toast.error('Only *.jpeg, *.jpg, *.png, *.gif files are allowed');
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      toast.error('File size must be under 3.1 MB');
      return;
    }
    setPendingAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLocalPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleField = (field: keyof ProfileFormData) => (value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const trimmed = form.full_name.trim();
    const parts = trimmed.split(/\s+/);
    const first_name = parts[0] ?? '';
    const last_name = parts.slice(1).join(' ');

    if (!first_name || !last_name) {
      toast.error('Enter both your first and last name.');
      return;
    }
    if (first_name.length > 100 || last_name.length > 100) {
      toast.error('First and last name must be 100 characters or less.');
      return;
    }

    setSaving(true);
    try {
      // Upload the avatar first (if a new file was picked) so the durable S3
      // key can be persisted in the same PUT. We send the key — not the
      // presigned URL — so it doesn't expire; useImageUrl re-signs on read.
      let avatarKeyToSave: string | undefined;
      if (pendingAvatarFile && user) {
        const upload = await uploadFile({
          file: pendingAvatarFile,
          resource_type: 'admin_avatar',
          resource_id: user.id,
        });
        avatarKeyToSave = upload.key;
      }

      const res = await authService.updateMe({
        first_name,
        last_name,
        role_id: form.role_id || null,
        department_id: form.department_id || null,
        location_id: form.location_id || null,
        ...(avatarKeyToSave ? { avatar_url: avatarKeyToSave } : {}),
      });

      // Sync local auth context so the header/avatar reflect the change.
      const returnedUser = res?.data?.user;
      const baseUser = returnedUser ?? {
        ...user!,
        first_name,
        last_name,
        full_name: `${first_name} ${last_name}`,
      };
      if (user) {
        const nextAuthUser: AuthUser = { ...user, ...baseUser };
        updateUser(nextAuthUser);
      }

      // Reset the pending upload state and refresh the displayed avatar from
      // the persisted value so we stop showing the stale base64 preview.
      const persistedAvatar = returnedUser?.avatar_url ?? avatarKeyToSave ?? avatarKey;
      setAvatarKey(persistedAvatar ?? null);
      setPendingAvatarFile(null);
      setLocalPreview(null);

      toast.success('Profile updated');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update profile. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const initials = `${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`;

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left — Avatar card */}
      <div className="lg:w-[300px] shrink-0">
        <div className="relative rounded-2xl overflow-hidden shadow-card">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-200 via-secondary-100 to-primary-100 dark:from-primary-900/60 dark:via-secondary-900/40 dark:to-primary-950/60" />
          <div className="relative flex flex-col items-center px-8 py-10 gap-4">
            {/* Avatar */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="relative group"
            >
              <div className="w-[120px] h-[120px] rounded-full overflow-hidden bg-primary-200 dark:bg-primary-800 flex items-center justify-center ring-4 ring-surface-primary shadow-lg">
                {avatarSrc ? (
                  <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-primary-700 dark:text-primary-350">{initials}</span>
                )}
              </div>
              <div className="absolute inset-0 rounded-full bg-black/30 opacity-60 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <CameraIcon className="w-7 h-7 text-white" />
              </div>
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />

            <p className="text-xs text-text-secondary text-center leading-relaxed">
              Allowed *.jpeg, *.jpg, *.png, *.gif<br />
              Max size of 3.1 MB
            </p>

            {/* Public profile toggle */}
            <div className="flex items-center justify-between w-full">
              <span className="text-sm font-medium text-text-primary">Public profile</span>
              <button
                type="button"
                onClick={() => setPublicProfile((v) => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  publicProfile ? 'bg-primary-500' : 'bg-neutral-300 dark:bg-neutral-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${publicProfile ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right — Profile form, then the email preference (saves on click, so it
          sits outside the form card and its Save button). */}
      <div className="flex-1 flex flex-col gap-6">
        <div className="relative bg-surface-primary rounded-2xl shadow-card p-6 flex flex-col gap-5">
          {(loading || saving) && (
            <div
              aria-busy="true"
              aria-live="polite"
              className="absolute inset-0 z-10 rounded-2xl bg-surface-primary/70 backdrop-blur-[1px] flex items-center justify-center"
            >
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                <span className="text-xs text-text-secondary">
                  {loading ? 'Loading profile…' : 'Saving changes…'}
                </span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FloatingField label="Full Name" value={form.full_name} onChange={handleField('full_name')} />
            <FloatingField
              label="Email"
              value={form.email}
              onChange={handleField('email')}
              type="email"
              readOnly
              readOnlyHint="Your sign-in email can't be changed here — it identifies your account."
            />
            <DepartmentSelect
              value={form.department_id}
              onChange={(id) => setForm(prev => ({ ...prev, department_id: id }))}
            />
            <RoleSelect
              value={form.role_id}
              onChange={(id) => setForm(prev => ({ ...prev, role_id: id }))}
            />
          </div>
          <LocationSelect
            value={form.location_id}
            onChange={(id) => setForm(prev => ({ ...prev, location_id: id }))}
          />

          <div className="flex justify-end pt-2">
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold rounded-full transition-colors disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>

        <MarketingConsentSwitch />
      </div>
    </div>
  );
};

export default GeneralSettings;
